import { createSlice } from "@reduxjs/toolkit";
import { baseApi } from "../../../app/baseApi";
import { apiTags } from "../../../app/store/apiTags";
import FormatService from "services/formatService";
import { storageInvoiceTypes } from "./storageInvoiceConsts";

const basePath = 'storageinvoice';

export const storageInvoiceApi = baseApi.injectEndpoints({
    reducerPath: 'loadInvoiceApi',
    endpoints: (builder: any) => ({
        getStorageInvoices: builder.query({
            query: (body: StorageInvoiceRequest) => ({
                url: `${basePath}?${Object.keys(body)?.filter(k => body[k]).map(k => `${k}=${body[k]}`)?.join('&')}`,

            }),
            providesTags: [apiTags.STORAGE_INVOICES],
            transformResponse: (response: Array<StorageInvoice>) => {
                return response.map(invoice => ({
                    ...invoice,
                }));
            }
        }),
        getStorageInvoicesList: builder.query({
            query: (body: StorageInvoiceRequest) => ({
                url: `${basePath}/list?${Object.keys(body)?.filter(k => body[k]).map(k => `${k}=${body[k]}`)?.join('&')}`,

            }),
            providesTags: [`${apiTags.STORAGE_INVOICES}_list`]
        }),
        getStorageInvoicePdf: builder.query({
            query: ({ invoiceDetailId }) => ({
                url: `${basePath}/pdf?invoiceDetailId=${invoiceDetailId}`,

            })
        }),
        sendStorageInvoicePdf: builder.mutation({
            query: ({ invoiceDetailId, toEmails, bccEmails }) => ({
                url: `${basePath}/pdf/send`,
                body: {
                    invoiceDetailId,
                    toEmails,
                    bccEmails
                },
                method: "POST",
            }),
        }),
        updateStorageInvoiceDetails: builder.mutation({
            query: (body: any) => ({
                url: `${basePath}/details`,
                method: "PUT",
                body
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES],
        }),
        updateStorageInvoiceTotals: builder.mutation({
            query: (body: any) => ({
                url: `${basePath}/totals`,
                method: "PUT",
                body
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES],
        }),
        approveStorageInvoice: builder.mutation({
            query: (id: any) => ({
                url: `${basePath}/approve`,
                method: "PUT",
                body: {
                    id
                }
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES],
        }),
        sendStorageInvoiceToQB: builder.mutation({
            query: (body) => ({
                url: `${basePath}/sendToQB`,
                method: "POST",
                body
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES],
        }),
        updatePCOInvoiceDetail: builder.mutation({
            query: (body: any) => ({
                url: `${basePath}/pco/details`,
                method: "PUT",
                body
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES],
        }),
        updateInvoiceActivity: builder.mutation({
            query: (body: any) => ({
                url: `${basePath}/pco/activity`,
                method: "PUT",
                body
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES],
        }),
        getAllInvoiceItems: builder.query({
            query: () => ({
                url: `${basePath}/items`
            })
        }),
        updateInvoiceStorageService: builder.mutation({
            query: (body: any) => ({
                url: `${basePath}/pco/storageService`,
                method: "PUT",
                body
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES],
        }),
        updatePCOInvoiceStatus: builder.mutation({
            query: (body) => ({
                url: `${basePath}/pco/status`,
                method: "POST",
                body: {
                    id: body.id,
                    statusId: body.statusId,
                }
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES]
        }),
        getInvoiceByVehicleId: builder.query({
            query: (vehicleId: number) => ({
                url: `${basePath}/GetByVehicleId?vehicleId=${vehicleId}`
            }),
        }),
        getInvoiceReports: builder.mutation({
            query: (reports: any) => ({
                url: `${basePath}/reports`,
                method: "POST",
                body: reports,
                responseHandler: async (response: any) => {
                    const blob = await response.blob();
                    const fileURL = window.URL.createObjectURL(blob);
                    const reportDate = (reports.invoiceCycleType == storageInvoiceTypes.WEEKLY) ? `${reports.cycleStart} - ${reports.cycleEnd}`
                        : FormatService.formatDateAndTimeWithFormat(new Date(reports.year, reports.month - 1), 'yyyy-MM');

                    let alink = document.createElement('a');
                    alink.href = fileURL;
                    alink.download = `Invoice_Reports_${reportDate}.zip`;
                    alink.click();
                },
                cache: "no-cache"
            })
        }),
        updateStorageInvoice: builder.mutation({
            query: (option: any) => ({
                url: `${basePath}/generateStorageInvoice`,
                method: "POST",
                body: option
            }),
            invalidatesTags: [apiTags.STORAGE_INVOICES, `${apiTags.STORAGE_INVOICES}_list`],
        }),
    })
});


export const {
    useGetStorageInvoicesQuery,
    useGetStorageInvoicesListQuery,
    useGetStorageInvoicePdfQuery,
    useUpdateStorageInvoiceDetailsMutation,
    useUpdateStorageInvoiceTotalsMutation,
    useApproveStorageInvoiceMutation,
    useSendStorageInvoiceToQBMutation,
    useUpdatePCOInvoiceDetailMutation,
    useSendStorageInvoicePdfMutation,
    useUpdateInvoiceActivityMutation,
    useGetAllInvoiceItemsQuery,
    useUpdateInvoiceStorageServiceMutation,
    useUpdatePCOInvoiceStatusMutation,
    useGetInvoiceByVehicleIdQuery,
    useGetInvoiceReportsMutation,
    useUpdateStorageInvoiceMutation
} = storageInvoiceApi;

const defaultFilterState = {
    'clientId': '',
    'month': '',
    'year': '',
    'startDate': '',
    'endDate': '',
    'cycle': ''
}

export const storageInvoiceStateSlice = createSlice({
    name: 'storageInvoiceStateSlice',
    initialState: {
        searchVal: '',

        filters: defaultFilterState
    },
    reducers: {
        setSearchVal: (state, action) => {
            state.searchVal = action.payload;
        },
        setFilters: (state, action) => {
            state.filters = action.payload;
        },
        setClient: (state, action) => {
            state.filters = { ...defaultFilterState, clientId: action.payload };
        },
        setFilter: (state, action) => {
            state.filters = { ...state.filters, [action.payload.key]: action.payload.value }
        },
    },
});

export const { setSearchVal, setFilters, setFilter, setClient } = storageInvoiceStateSlice.actions;
